import { makeAutoObservable, toJS } from 'mobx';
import io, { Socket } from 'socket.io-client';
import { Env } from '../../env';
import { AppRootStore } from '../store';
import { User } from '../../types/user';
import { t } from 'i18next';

class SocketStore {
    root: AppRootStore;

    constructor(root: AppRootStore) {
        makeAutoObservable(this);
        this.root = root;
    }

    _socket: typeof Socket | undefined;
    _connected = false;
    _keepAlive = false;
    _initialized = false;
    _error = false;
    _error_type = '';
    _error_message = '';

    get socket() {
        return this._socket;
    }

    get connected() {
        return this._connected;
    }

    get keepAlive() {
        return this._keepAlive;
    }

    set keepAlive(value: boolean) {
        this._keepAlive = value;
    }

    connect = (user?: User) => {
        console.log('connecting...', toJS(this._socket));

        if (this._socket && !this._connected) {
            return this._socket?.open();
        }

        // The server still only reads userId today (the token is ignored),
        // so sending both is safe against the current backend. Once the
        // backend verifies the token itself, userId becomes redundant but
        // harmless to keep sending - see the security review this came out of.
        this._socket = io(Env.SocketUrl, {
            autoConnect: true,
            query: { userId: user?.id, token: this.root.localStore.session.accessToken },
            transports: ['websocket'],
            reconnection: true,
            forceNew: true,
        });

        this._socket.on('connect', () => {
            this._connected = true;
            console.log('connected', this._socket);

            if (this._initialized) {
                return;
            }

            this._socket?.on('ping', (error: any) => {
                this._error = false;
                this._error_message = error?.message;
                this._error_type = '';
                console.log('ping');
            });

            this._socket?.on('disconnect', (error: any) => {
                this._connected = false;
                this._error = true;
                this._error_message = error?.message;
                this._error_type = 'warn';
                console.log('disconnected', this._socket);
            });

            this._socket?.on('error', (error: any) => {
                this._error = true;
                this._error_message = error?.message;
                this._error_type = 'error';
                console.log('error', error);
            });

            this.root.chatStore.init();
            this.root.channelSocketStore.init();
        });

        this._socket?.on('connect_error', (error: any) => {
            console.log('connect_error', error?.message);
        });
    };

    disconnect = () => {
        if (this._socket) {
            this._socket.disconnect();
        } else {
            console.log('socket is null');
        }
    };

    dispose = () => {
        this._connected = false;
        this._keepAlive = false;
        this._initialized = false;
        this._error = false;
        this._error_type = '';
        this._error_message = '';

        this._socket?.removeAllListeners();

        this._socket = io();
    };
}

export default SocketStore;
